import React, { useEffect, useState } from "react";
import { API } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, Trash2, Save, CalendarClock } from "lucide-react";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

export const AvailabilityEditor = () => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchAvailability = async () => {
      try {
        const res = await fetch(`${API}/council/availability`, { credentials: "include" });
        if (res.ok) {
          const data = await res.json();
          setRules(data.rules || []);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchAvailability();
  }, []);

  const addRange = (day) => {
    setRules((r) => [...r, { day, start: "10:00", end: "12:00" }]);
  };

  const removeRange = (index) => {
    setRules((r) => r.filter((_, i) => i !== index));
  };

  const updateRange = (index, field, value) => {
    setRules((r) => r.map((rule, i) => (i === index ? { ...rule, [field]: value } : rule)));
  };

  const save = async () => {
    for (const r of rules) {
      if (r.start >= r.end) {
        toast.error(`${DAYS[r.day]}: start time must be before end time`);
        return;
      }
    }
    setSaving(true);
    try {
      const res = await fetch(`${API}/council/availability`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ rules }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Failed to save");
      }
      toast.success("Availability saved — explorers can now book these slots");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="card-flat text-center py-8 text-[#52525B] text-sm">Loading availability...</div>;
  }

  return (
    <div className="space-y-4" data-testid="availability-editor">
      <div className="card-flat">
        <div className="flex items-start gap-3 mb-6">
          <CalendarClock size={18} className="text-[#064E3B] mt-0.5" />
          <div>
            <h3 className="text-lg font-medium text-[#0F172A]" style={{ fontFamily: "'Playfair Display', serif" }}>
              Weekly Availability
            </h3>
            <p className="text-xs text-[#52525B] mt-1">
              Set your weekly hours (IST). Explorers can only book sessions inside these windows — booked slots are hidden automatically.
            </p>
          </div>
        </div>

        <div className="space-y-3">
          {DAYS.map((dayName, dayIdx) => {
            const dayRules = rules.map((r, i) => ({ ...r, _index: i })).filter((r) => r.day === dayIdx);
            return (
              <div key={dayName} className="flex flex-col sm:flex-row sm:items-start gap-2 border-b border-[#E4E4E1] pb-3 last:border-0">
                <div className="w-28 shrink-0 pt-1.5">
                  <span className={`text-sm font-semibold ${dayRules.length ? "text-[#0F172A]" : "text-[#A1A1AA]"}`}>{dayName}</span>
                </div>
                <div className="flex-1 space-y-2">
                  {dayRules.length === 0 && <p className="text-xs text-[#A1A1AA] pt-2">Unavailable</p>}
                  {dayRules.map((r) => (
                    <div key={r._index} className="flex items-center gap-2">
                      <input
                        type="time"
                        value={r.start}
                        onChange={(e) => updateRange(r._index, "start", e.target.value)}
                        className="border border-[#E4E4E1] bg-white text-sm px-2 py-1.5 rounded-sm focus:outline-none focus:border-[#0F172A]"
                        data-testid={`availability-start-${dayIdx}`}
                      />
                      <span className="text-xs text-[#A1A1AA]">to</span>
                      <input
                        type="time"
                        value={r.end}
                        onChange={(e) => updateRange(r._index, "end", e.target.value)}
                        className="border border-[#E4E4E1] bg-white text-sm px-2 py-1.5 rounded-sm focus:outline-none focus:border-[#0F172A]"
                        data-testid={`availability-end-${dayIdx}`}
                      />
                      <button
                        onClick={() => removeRange(r._index)}
                        className="text-red-400 hover:text-red-600 transition-colors p-1"
                        data-testid={`availability-remove-${dayIdx}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => addRange(dayIdx)}
                  className="flex items-center gap-1 text-xs font-semibold text-[#064E3B] hover:text-[#0F172A] transition-colors shrink-0 pt-2"
                  data-testid={`availability-add-${dayIdx}`}
                >
                  <Plus size={13} /> Add hours
                </button>
              </div>
            );
          })}
        </div>

        <div className="mt-6 flex items-center gap-3">
          <button
            onClick={save}
            disabled={saving}
            className="btn-primary text-sm flex items-center gap-2"
            data-testid="save-availability-btn"
          >
            <Save size={14} /> {saving ? "Saving..." : "Save Availability"}
          </button>
          {rules.length === 0 && (
            <p className="text-xs text-[#A1A1AA]">No hours set — explorers can request any time until you add windows.</p>
          )}
        </div>
      </div>
    </div>
  );
};
